import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Section } from '@/components/compendium';

import {
  getMainTariffOffer,
  getTariffOfferComparator,
  getMatchedTariffOffers,
  getStairSchemaClassForIndex,
  getHighlightedProduct,
  interpolate,
  formatDate,
  isExpansionPeriodEndInFuture,
  formatDateWithoutDate
} from '@/utils';

import {
  TariffCatalogType,
  Feature,
  OfferType,
  ReduxStateType,
  TariffOfferType,
  FilterValuesType,
  AvailabilityCheckMetaType,
  CmsPageContentType,
  CmsPageContentIdentifier,
  TariffPositionsType, OverviewDisplayPrices,
} from '@/types/av-check';

import {
  groupByMainAlternate,
} from '@/utils/offerGrouping';
import TechnologySelectionModal from '@/components/compendium/Modal/TechnologySelectionModal';
import TariffCatalogItem from './TariffCatalogItem';
import TariffCatalogWrapper from './TariffCatalogWrapper';
import FTTHSpecificationModal from '@/components/compendium/Modal/FTTHSpecificationModal';
import StaticContentModal from "@/components/modal/static-content-modal";

export interface StateProps {
  cmsContent: CmsPageContentType,
  tariffOffers: TariffOfferType[],
  filterValues: FilterValuesType,
  meta: AvailabilityCheckMetaType,
  enabledFeatures: Feature[],
  overviewDisplayPrices: OverviewDisplayPrices[]
}

export interface TariffCatalogProps {
  isRedirectedFromFtthSelection: boolean,
  setRedirectedFromFtthSelection: Dispatch<SetStateAction<boolean>>,
  expansionStatus:string,
  serviceUrl : string,
  shopContext: string,
  selectedPack: string,
  selectedRouter: string,
  hardwareOfferName: string,
  isCmsCall:boolean,
  isCciTheme: boolean,
}

const TariffCatalog = ({
  cmsContent,
  tariffOffers,
  filterValues,
  meta,
  enabledFeatures,
  isRedirectedFromFtthSelection,
  setRedirectedFromFtthSelection,
  expansionStatus,
  serviceUrl,
  shopContext,
  selectedPack,
  selectedRouter,
  hardwareOfferName,
  isCmsCall,
  overviewDisplayPrices,
  isCciTheme
}: StateProps & TariffCatalogProps) => {
  const taroCommissionsShopEnabled = ((!meta.cciTheme && enabledFeatures.includes(Feature.TARO_COMMISSIONS)))
        || false;
  const taroCommissionsCciEnabled = ((meta.cciTheme && enabledFeatures.includes(Feature.TARO_COMMISSIONS_CCI)))
        || false;
  const taroPricingPromotionEnabled = enabledFeatures.includes(Feature.TARO_PRICING_PROMOTION) || false;

  const [ftthModelRedirectionState, setFtthModelRedirectionState] = useState(false);
  const [ftthSelectedTariffState, setFtthSelectedTariffState] = useState<TariffOfferType>();
  const [ftthmodalTariffPositionsState, setFtthmodalTariffPositionsState] = useState<TariffPositionsType>();

  const matchedTariffOffers = getMatchedTariffOffers(tariffOffers, filterValues)
    .filter((item) => item.simOnly)
    .sort(getTariffOfferComparator());

  const tariffCatalogs: TariffCatalogType[] = groupByMainAlternate(matchedTariffOffers, meta.selectedBandwidth);

  const moreThanOneAccessTechnologyAvailable = (offer: TariffCatalogType) => (offer.coaxTariffOffer && offer.fmsTariffOffer)
        || (offer.coaxTariffOffer && offer.dslTariffOffer)
        || (offer.fmsTariffOffer && offer.dslTariffOffer)
        || (offer.fmsTariffOffer && offer.ftthTariffOffer)
        || (offer.dslTariffOffer && offer.ftthTariffOffer)
        || (offer.coaxTariffOffer && offer.ftthTariffOffer);

  const productTypeLabels = {
    DSL: cmsContent[CmsPageContentIdentifier.TariffProductTypeHintDSL],
    FMS: cmsContent[CmsPageContentIdentifier.TariffProductTypeHintFMS],
  };

  const technologyPricingLabels = {
    DISCOUNT: cmsContent[CmsPageContentIdentifier.TechnologyPricingDiscountLabel],
    SURCHARGE: cmsContent[CmsPageContentIdentifier.TechnologyPricingSurchargeLabel],
    MONTHLYLIST: cmsContent[CmsPageContentIdentifier.TechnologyPricingMonthlyList]
  };

  const technologyPricingVariationHintlabels = {
    DISPLAYED_TARIFF: cmsContent[CmsPageContentIdentifier.TechnologyPricingProductVariationHint],
    VARIED_TARIFFS: cmsContent[CmsPageContentIdentifier.TechnologyPricingProductVariations],
    VARIATION_HINT_LABEL: cmsContent[CmsPageContentIdentifier.TechnologyPricingPriceVariationHintLabel],
  };

  const [currentlySelectedTariffGroup, setCurrentlySelectedTariffGroup] = useState(tariffCatalogs[0]);
  const highlightedProduct = getHighlightedProduct(tariffCatalogs) || tariffCatalogs[0];

  let earliestAvailabilityDateVmOngoingCmsContent: string|undefined = undefined;

  useEffect(() => {
    setCurrentlySelectedTariffGroup(prevState => {
      if (isRedirectedFromFtthSelection && prevState.ftthTariffOffer != undefined) {
        return tariffCatalogs.filter(value =>
          value.ftthTariffOffer && prevState &&
            prevState.ftthTariffOffer &&
            value.ftthTariffOffer?.tariffVariationCode == prevState.ftthTariffOffer.tariffVariationCode,
        )[0];
      } else {
        return tariffCatalogs[0];
      }
    } );
  }, [tariffOffers]);

    return (
    <Section type={Section.Type.DECENT}>
      <TariffCatalogWrapper
        key={matchedTariffOffers.map((to) => to.offerName).join()}
        slider={!meta.cciTheme}
        isCmsCall = {isCmsCall}
      >
        {tariffCatalogs.map((tariffCatalog, index) => {
          const mainOffer = getMainTariffOffer(tariffCatalog);
          const highlightProduct = highlightedProduct === tariffCatalog;
          const { offerType } = mainOffer;
          const productType = mainOffer.type;
          const productTypeLabel = productType ? productTypeLabels[productType] : undefined;
          const highlighted = meta.selectedBandwidth ? (offerType === OfferType.SELECTED) : highlightProduct;
          const catalogKey = mainOffer.externalId || mainOffer.offerName;
          const overViewPrice = overviewDisplayPrices.filter(overviewDisplayPrice => overviewDisplayPrice.tariffVariations.includes(mainOffer.tariffVariationCode))
              .find((overviewDisplayPrice) => overviewDisplayPrice);
          const isDtagFtthTariff : boolean|undefined  = tariffCatalog.ftthTariffOffer &&
              tariffCatalog.ftthTariffOffer.accessType === "DTAG-FTTH-ETH" ;
          const dtagEarliestActivationStartDate : string|undefined = tariffCatalog.ftthTariffOffer &&
              tariffCatalog.ftthTariffOffer.technologyAvailability && tariffCatalog.ftthTariffOffer.technologyAvailability.expansionPeriodStart;
          const dtagEarliestActivationDate : string|undefined = tariffCatalog.ftthTariffOffer && tariffCatalog.ftthTariffOffer.technologyAvailability
              && tariffCatalog.ftthTariffOffer.technologyAvailability.expansionPeriodEnd &&
              isExpansionPeriodEndInFuture(tariffCatalog.ftthTariffOffer.technologyAvailability.expansionPeriodEnd) ?
              tariffCatalog.ftthTariffOffer.technologyAvailability.expansionPeriodEnd : undefined;
          const uggEarliestAvailabilityDate: string|undefined = tariffCatalog.ftthTariffOffer && tariffCatalog.ftthTariffOffer.earliestActivationDate
            ? formatDate(tariffCatalog.ftthTariffOffer.earliestActivationDate)
            : undefined;
          const formattedStartDate: string | undefined = dtagEarliestActivationStartDate ?
              formatDateWithoutDate(dtagEarliestActivationStartDate) : undefined;
          const formattedEndDate: string | undefined = dtagEarliestActivationDate ?
              formatDateWithoutDate(dtagEarliestActivationDate) : undefined;
          // Ensure infoIcon appears once between the start and end dates
          let earliestAvailabilityDate: string | undefined = '';
          let infoIconModelId = 'vmOngoing_modal_' + index;
          const infoIconMarkup = isDtagFtthTariff && dtagEarliestActivationDate &&
          tariffCatalog.ftthTariffOffer?.technologyAvailability.expansionStatus === "vvmOngoing"
              ? `<a class='info' data-toggle='modal' data-target='#${infoIconModelId}'></a>`
              : "";
          if (isDtagFtthTariff && formattedStartDate && formattedEndDate) {
            earliestAvailabilityDate = `${formattedStartDate} ${infoIconMarkup} <br/> und ${formattedEndDate}`;
          } else {
            earliestAvailabilityDate = uggEarliestAvailabilityDate;
          }
          // font weight 700 could not be applied in cms
          const boldDate = ["<span style='font-weight: 700;'/>", earliestAvailabilityDate, '</span>'].join('');
          const earliestAvailabilityDateCmsContent: string = earliestAvailabilityDate
              ? `<tef-promo-box cloak color-scheme="bg-light-grey">
                   <div slot="title">
                      ${cmsContent[CmsPageContentIdentifier.FtthExpectedExpansionText]}
                      ${interpolate(cmsContent[CmsPageContentIdentifier.ExpectedExpansionDateText],{ date: boldDate})} 
                      ${cmsContent[CmsPageContentIdentifier.FtthFreeConversionServiceText]}
                  </div>
               </tef-promo-box>`
              : '';
          const homeIdPossible: boolean|undefined = tariffCatalog.ftthTariffOffer
            ? tariffCatalog.ftthTariffOffer?.technologyAvailability.homeIdPossible
            : undefined;
          return (
            <TariffCatalogItem
                            // eslint-disable-next-line react/no-array-index-key
              stairSchemaClass={!meta.cciTheme ? getStairSchemaClassForIndex(index, tariffCatalogs.length) : undefined}
              taroCommissionsShop={taroCommissionsShopEnabled}
              taroCommissionsCci={taroCommissionsCciEnabled}
              key={catalogKey}
              tariffCatalog={tariffCatalog}
              tariffButtonLabel={moreThanOneAccessTechnologyAvailable(tariffCatalog) ? cmsContent[CmsPageContentIdentifier.TechnologySelectionButton] : cmsContent[CmsPageContentIdentifier.TariffButton]}
              highlightLabel={highlighted ? cmsContent[CmsPageContentIdentifier.TariffRecommendation] : undefined}
              productTypeLabel={productTypeLabel}
              availableAsLabel={cmsContent[CmsPageContentIdentifier.AvailableAsLabel]}
              dslOptionLabel={cmsContent[CmsPageContentIdentifier.DslOptionLabel]}
              cmsContent={cmsContent}
              coaxOptionLabel={cmsContent[CmsPageContentIdentifier.CoaxOptionLabel]}
              fmsOptionLabel={cmsContent[CmsPageContentIdentifier.FmsOptionLabel]}
              upsellLabel={cmsContent[CmsPageContentIdentifier.UpsellLabel]}
              downsellLabel={cmsContent[CmsPageContentIdentifier.DownsellLabel]}
              selectedBandwidthMatchLabel={cmsContent[CmsPageContentIdentifier.SelectedBandwidthMatchLabel]}
              ftthEarliestAvailabilityDate={earliestAvailabilityDateCmsContent}
              ftthPreconnected={homeIdPossible}
              ftthExpansionStatus={expansionStatus}
              isCciTheme = {isCciTheme}
              technologyPricingLabels={technologyPricingLabels}
              technologyPricingVariationHintlabels={technologyPricingVariationHintlabels}
              tariffButtonClickEvent={() => {setCurrentlySelectedTariffGroup(tariffCatalog);setRedirectedFromFtthSelection(false);}}
              setFtthModelRedirectionState={setFtthModelRedirectionState}
              displayPrice={overViewPrice ? overViewPrice.tariffDisplayPrice : undefined}
              enabledFeatures={enabledFeatures}
            />
          );
        })}

      </TariffCatalogWrapper>
      {matchedTariffOffers.length === 0
            && (
            <div>{cmsContent[CmsPageContentIdentifier.NoTariffForSelection]}</div>
            )}
      <div dangerouslySetInnerHTML={{ __html: cmsContent[CmsPageContentIdentifier.PriceHint] }} />
      {(currentlySelectedTariffGroup) && (
      <TechnologySelectionModal
        tariffOfferGroup={currentlySelectedTariffGroup}
        taroPricingPromotionEnabled = {taroPricingPromotionEnabled}
        setFtthModelRedirectionState={setFtthModelRedirectionState}
        setFtthSelectedTariffState={setFtthSelectedTariffState}
        setFtthmodalTariffPositionsState={setFtthmodalTariffPositionsState}
        isRedirectedFromFtthSelection={isRedirectedFromFtthSelection}
        ftthExpansionStatus={expansionStatus}
        isDtagToggleShopEnabled = {(!meta.cciTheme && enabledFeatures.includes(Feature.DTAG_FTTH_WEBSHOP))}
        isDtagToggleCciEnabled = {(meta.cciTheme && enabledFeatures.includes(Feature.DTAG_FTTH_CCI))}
        isDtagGeneralToggleEnabled = {(enabledFeatures.includes(Feature.DTAG_GENERAL))}
        isDtagGuiEnhancementsToggleEnabledForShop = {(!meta.cciTheme && enabledFeatures.includes(Feature.DTAG_GUI_ENHANCEMENTS))}
        isDtagGuiEnhancementsToggleEnabledForCci = {(meta.cciTheme && enabledFeatures.includes(Feature.DTAG_GUI_ENHANCEMENTS_CCI))}
        isDisplayPricingEnabled = {((!meta.cciTheme && enabledFeatures.includes(Feature.TARO_DISPLAY_PRICES)) ||
            (meta.cciTheme && enabledFeatures.includes(Feature.TARO_DISPLAY_PRICES_CCI)))}
        isTariffShortNameToggleEnabled={enabledFeatures.includes(Feature.DISPLAY_TARIFF_SHORT_NAME)}
        isCciTheme = {isCciTheme}
      />
      )}
      <FTTHSpecificationModal
          ftthModelRedirectionState={ftthModelRedirectionState}
          ftthSelectedTariffState={ftthSelectedTariffState}
          ftthmodalTariffPositionsState={ftthmodalTariffPositionsState}
          setRedirectedFromFtthSelection={setRedirectedFromFtthSelection}
          isRedirectedFromFtthSelection={isRedirectedFromFtthSelection}
          ftthExpansionStatus={expansionStatus}
          serviceUrl={serviceUrl}
          shopContext={shopContext}
          selectedPack={selectedPack}
          selectedRouter={selectedRouter}
          hardwareOfferName={hardwareOfferName}
          isDtagGuiEnhancementsToggleEnabledForShop = {(!meta.cciTheme && enabledFeatures.includes(Feature.DTAG_GUI_ENHANCEMENTS))}
          isDtagGuiEnhancementsToggleEnabledForCci = {(meta.cciTheme && enabledFeatures.includes(Feature.DTAG_GUI_ENHANCEMENTS_CCI))}
      />
      {tariffCatalogs.map((tariffCatalog, index) => {
        let infoIconModelId = 'vmOngoing_modal_' + index;
        const dtagEarliestActivationDate: string | undefined = tariffCatalog.ftthTariffOffer && tariffCatalog.ftthTariffOffer.technologyAvailability
        && tariffCatalog.ftthTariffOffer.technologyAvailability.expansionPeriodEnd &&
        isExpansionPeriodEndInFuture(tariffCatalog.ftthTariffOffer.technologyAvailability.expansionPeriodEnd) ?
          tariffCatalog.ftthTariffOffer.technologyAvailability.expansionPeriodEnd : undefined;
        earliestAvailabilityDateVmOngoingCmsContent = dtagEarliestActivationDate
          ? interpolate(cmsContent[CmsPageContentIdentifier.FtthEarliestAvailabilityVmOngoingDate], {
            date: ["<span style='font-weight: 700;'/>", formatDate(dtagEarliestActivationDate), '</span>'].join(''),
          })
          : '';
        return (
            <>
              {dtagEarliestActivationDate &&
                  <StaticContentModal
                      openerId={infoIconModelId}
                      displayCloseButton
                  >
                    {earliestAvailabilityDateVmOngoingCmsContent &&
                        <p dangerouslySetInnerHTML={{ __html: earliestAvailabilityDateVmOngoingCmsContent }}/>}
                  </StaticContentModal>
              }
            </>
        );
      })}
    </Section>

  );
};

const mapStateToProps = (state: ReduxStateType) => ({
  cmsContent: state.cmsContent,
  tariffOffers: state.result?.tariffOffers || [],
  filterValues: state.filterValues,
  meta: state.meta,
  enabledFeatures: state.enabledFeatures,
  address:state.result?.address,
  overviewDisplayPrices: state.result?.overviewDisplayPrices || []
});

export default connect(mapStateToProps)(TariffCatalog);
